import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  packNotificationAction,
  schoolDetailAction,
} from "../../Redux/Action/authAction";
import { organisationInvitesListAction } from "../../Redux/Action/organisationAction";
import { getMemberSidebarConfig, getOrganisationSidebarConfig, getAccountSidebarConfig } from "../../config/sidebarConfig";
import { getOrganisationRoleAccess, isOrganisationGroupUser, getActiveGroup } from "../../OrganisationGroups/utils/roleAccess";
import SchoolInvitesOffcanvas from "./SchoolInvitesOffcanvas";

export default function Sidebar({ isActive, setIsActive, setShow, show }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showInvitesOffcanvas, setShowInvitesOffcanvas] = useState(false);

  const schoolData = useSelector((state) => state?.authSlice?.schoolDetail);
  const schoolDetailStatus = useSelector((state) => state?.authSlice?.schoolDetailStatus);
  const Notification = useSelector((state) => state?.authSlice?.notification);
  const notificationStatus = useSelector((state) => state?.authSlice?.notificationStatus);
  const userData = useSelector((state) => state?.authSlice.userData?.payload);

  const [orgSectionOpen, setOrgSectionOpen] = useState(false);
  const [memberSectionOpen, setMemberSectionOpen] = useState(false);
  const [accountSectionOpen, setAccountSectionOpen] = useState(false);
  const [viewedInviteIds, setViewedInviteIds] = useState(() => {
    try {
      const stored = sessionStorage.getItem("viewedInviteIds");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [currentPendingIds, setCurrentPendingIds] = useState([]);

  const hasPendingInvite = currentPendingIds.some((id) => !viewedInviteIds.includes(id));

  useEffect(() => {
    const handleInvitesViewed = () => {
      setViewedInviteIds((prev) => {
        const newIds = Array.from(new Set([...prev, ...currentPendingIds]));
        sessionStorage.setItem("viewedInviteIds", JSON.stringify(newIds));
        return newIds;
      });
    };
    window.addEventListener("invites-viewed", handleInvitesViewed);
    return () => window.removeEventListener("invites-viewed", handleInvitesViewed);
  }, [currentPendingIds]);

  const access = getOrganisationRoleAccess(userData, schoolData);


  const isDualAccess = access.hasMemberAccess && access.isGroupUser;

  useEffect(() => {
    if (isDualAccess) {
      const isAccountRoute = ["/settings", "/help-support", "/report-issue"].some(path => location.pathname.includes(path));

      if (isAccountRoute) {
        setOrgSectionOpen(false);
        setMemberSectionOpen(false);
        setAccountSectionOpen(true);
      } else if (location.pathname.startsWith("/organisations")) {
        setOrgSectionOpen(true);
        setMemberSectionOpen(false);
        setAccountSectionOpen(false);
      } else {
        setOrgSectionOpen(false);
        setMemberSectionOpen(true);
        setAccountSectionOpen(false);
      }
    } else {
      setOrgSectionOpen(true);
      setMemberSectionOpen(true);
      setAccountSectionOpen(true);
    }
  }, [isDualAccess, location.pathname]);

  const gotoDashBoard = () => {
    if (access.hasMemberAccess && !location.pathname.startsWith("/organisations")) {
      navigate(`/dashboard`);
    } else if (access.isGroupUser) {
      navigate(`/organisations/dashboard`);
    } else {
      navigate(`/dashboard`);
    }
  };

  useEffect(() => {
    if (!userData?._id || isOrganisationGroupUser(userData)) return;

    if (schoolDetailStatus === "idle") {
      dispatch(schoolDetailAction());
    }

    if (userData?.role === "School" && notificationStatus === "idle") {
      dispatch(packNotificationAction());
    }
  }, [dispatch, notificationStatus, schoolDetailStatus, userData]);

  useEffect(() => {
    if (userData?.role === "School") {
      dispatch(organisationInvitesListAction({ page: 0, limit: 10, search: "" }))
        .then((response) => {
          if (response?.payload?.data?.invites) {
            const pendingInvites = response.payload.data.invites.filter(
              (invite) => invite.inviteStatus === "INVITED"
            );
            setCurrentPendingIds(pendingInvites.map((i) => i._id));
          }
        });
    }
  }, [dispatch, userData?.role, schoolDetailStatus]);

  useEffect(() => {
    if (showInvitesOffcanvas) {
      setViewedInviteIds((prev) => {
        const newIds = Array.from(new Set([...prev, ...currentPendingIds]));
        sessionStorage.setItem("viewedInviteIds", JSON.stringify(newIds));
        return newIds;
      });
    }
  }, [showInvitesOffcanvas, currentPendingIds]);

  const hasUnseenInvite = hasPendingInvite;

  const memberConfig = getMemberSidebarConfig({
    access,
    userData,
    schoolData,
    Notification,
    showInvitesOffcanvas,
    setShowInvitesOffcanvas,
    hasPendingInvite,
    hasUnseenInvite,
  });

  const orgConfig = getOrganisationSidebarConfig({ access });

  const isOrgRoute = location.pathname.startsWith("/organisations");
  const accountConfig = getAccountSidebarConfig({ access, isOrgRoute });

  const renderMenuItems = (config) => {
    return config.filter(item => item.show).map((item, idx) => {
      const isNoSpace = ["Collections", "Strategy Packs", "Parents", "Help & Support", "Settings"].includes(item.label);

      if (item.isButton) {
        return (
          <div className={isNoSpace ? "" : "side-btm-space"} key={idx}>
            <button
              type="button"
              className={item.isActive ? "menu-btn bar_active" : "menu-btn"}
              onClick={item.onClick}
            >
              {item.icon}
              <p>
                <span style={{ position: "relative" }}>
                  {item.label}
                  {item.notificationDotYellow && <span className="sidebar-notification-dot-yellow"></span>}
                </span>
              </p>
            </button>
          </div>
        );
      }

      if (item.isExternal) {
        return (
          <div className={isNoSpace ? "" : "side-btm-space"} key={idx}>
            <Link
              to={item.path}
              target="_blank"
              rel="noopener noreferrer"
              className={location.pathname === item.path ? "menu-btn bar_active" : "menu-btn"}
            >
              {item.icon}
              <p>{item.label}</p>
            </Link>
          </div>
        );
      }

      const isActiveRoute = item.activePaths
        ? item.activePaths.includes(location.pathname)
        : location.pathname === item.path;

      return (
        <div className={isNoSpace ? "" : "side-btm-space"} key={idx}>
          <Link
            to={item.path}
            className={isActiveRoute ? "menu-btn bar_active" : "menu-btn"}
          >
            {item.icon}
            <p>
              <span style={{ position: "relative" }}>
                {item.label}
                {item.notificationDot && <span className="sidebar-notification-dot"></span>}
              </span>
            </p>
          </Link>
        </div>
      );
    });
  };

  return (
    <aside>
      <div className="sidebar">
        <div className="toggle-icon" onClick={() => setShow(!show)}>
          <img src={require("../../Assets/Images/toggle.svg").default} alt="Toggle sidebar" />
        </div>
        <div
          className="logo-side"
          onClick={gotoDashBoard}
          style={{ cursor: "pointer" }}
        >
          <img
            className="small"
            src={require("../../Assets/Images/clogo.png")}
            alt="Logo small"
          />
          <img
            className="big"
            src={require("../../Assets/Images/main-logo.png")}
            alt="Logo big"
          />
        </div>
        <div className="side-menu">

          {access.hasMemberAccess && (
            <div className="sidebar-section-container">
              {!show && isDualAccess && (
                <div
                  className="organisation-sidebar-section collapsible-section-header"
                  onClick={() => setMemberSectionOpen(!memberSectionOpen)}
                  style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}
                >
                  <p style={{ margin: 0, fontWeight: "bold", color: "#8b9eb7", fontSize: "13px", textTransform: "uppercase" }}>{schoolData?.schoolName || userData?.schoolName || "Your Organisation"}</p>
                  <span style={{ fontSize: "12px", color: "#666" }}>{memberSectionOpen ? "▲" : "▼"}</span>
                </div>
              )}
              {(!isDualAccess || memberSectionOpen || show) && (
                <div>
                  {renderMenuItems(memberConfig)}
                </div>
              )}
            </div>
          )}

          {access.isGroupUser && (
            <div className="sidebar-section-container" style={{ marginTop: access.hasMemberAccess && !show ? '20px' : '0' }}>
              {!show && (
                <div
                  className="organisation-sidebar-section collapsible-section-header"
                  onClick={() => isDualAccess && setOrgSectionOpen(!orgSectionOpen)}
                  style={{ cursor: isDualAccess ? "pointer" : "default", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}
                >
                  <p style={{ margin: 0, fontWeight: "bold", color: "#8b9eb7", fontSize: "13px", textTransform: "uppercase" }}>{getActiveGroup(userData)?.name || "Your Organisation Group"}</p>
                  {isDualAccess && <span style={{ fontSize: "12px", color: "#666" }}>{orgSectionOpen ? "▲" : "▼"}</span>}
                </div>
              )}
              {(!isDualAccess || orgSectionOpen || show) && (
                <div>
                  {renderMenuItems(orgConfig)}
                </div>
              )}
            </div>
          )}


          <div className="sidebar-section-container" style={{ marginTop: isDualAccess && !show ? '20px' : 'auto' }}>
            {!show && (
              <div
                className="organisation-sidebar-section collapsible-section-header"
                onClick={() => isDualAccess && setAccountSectionOpen(!accountSectionOpen)}
                style={{ cursor: isDualAccess ? "pointer" : "default", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}
              >
                <p style={{ margin: 0, fontWeight: "bold", color: "#8b9eb7", fontSize: "13px", textTransform: "uppercase" }}>Your Account</p>
                {isDualAccess && <span style={{ fontSize: "12px", color: "#666" }}>{accountSectionOpen ? "▲" : "▼"}</span>}
              </div>
            )}
            {(!isDualAccess || accountSectionOpen || show) && (
              <div>
                {renderMenuItems(accountConfig)}
              </div>
            )}
          </div>

        </div>
      </div>
      <SchoolInvitesOffcanvas
        show={showInvitesOffcanvas}
        handleClose={() => setShowInvitesOffcanvas(false)}
      />
    </aside>
  );
}
